import {
  createContext,
  Fragment,
  ReactElement,
  ReactNode,
  CSSProperties,
  useState,
  useContext,
} from 'react';

import { Tab as HeadlessTab } from '@headlessui/react';
import styled from 'styled-components';

import { Badge } from '@components/badge';
import { IconProps } from '@components/icons';
import { Stack } from '@components/stack';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

type TabsContextValue = {
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
};

const TabsContext = createContext<TabsContextValue | null>(null);

export const useTabsContext = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('useTabsContext must be used within a TabsProvider');
  }
  return context;
};

export const TabsProvider = ({
  children,
  defaultIndex = 0,
}: {
  children: ReactNode;
  defaultIndex?: number;
}) => {
  const [selectedIndex, setSelectedIndex] = useState(defaultIndex);
  return (
    <TabsContext.Provider value={{ selectedIndex, setSelectedIndex }}>
      {children}
    </TabsContext.Provider>
  );
};

export interface TabItem {
  icon: ReactElement<IconProps>;
  count?: number;
  active?: boolean;
  label: string;
  content: ReactNode;
  onClick?: () => void;
}

export interface TabProps {
  items: Array<TabItem>;
  selectedIndex?: number;
  tabWidth?: number;
  contentWidth?: number;
  panelsStyle?: CSSProperties;
}

export function Tabs(props: TabProps) {
  const {
    items,
    selectedIndex: selectedIndexProp,
    tabWidth = 3,
    contentWidth = 6,
    panelsStyle,
  } = props;
  const context = useContext(TabsContext);
  const [localIndex, setLocalIndex] = useState(0);
  const selectedIndex = selectedIndexProp ?? context?.selectedIndex ?? localIndex;
  const setSelectedIndex = context?.setSelectedIndex ?? setLocalIndex;

  // Defaults (12-col grid) Tabs 3 columns (1-4), Content 6 columns (4-10)
  const tabEnd = 1 + tabWidth;
  const contentEnd = tabEnd + contentWidth;

  return (
    <HeadlessTab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex} vertical>
      <Grid $tabEnd={tabEnd} $contentEnd={contentEnd}>
        <HeadlessTab.List as={TabContainer}>
          {items.map((item) => (
            <HeadlessTab key={item.label} as={Fragment}>
              <StyledTabItem onClick={item.onClick}>
                <Stack orientation="horizontal" space={2} align="center">
                  {item.icon}
                  <span>{item.label}</span>
                </Stack>
                {item.count !== undefined && (
                  <Badge variant={item.active ? 'green' : 'neutral'} size="md" text={item.count} />
                )}
              </StyledTabItem>
            </HeadlessTab>
          ))}
        </HeadlessTab.List>
        <HeadlessTab.Panels style={panelsStyle}>
          {items.map((item) => (
            <HeadlessTab.Panel key={item.label}>{item.content}</HeadlessTab.Panel>
          ))}
        </HeadlessTab.Panels>
      </Grid>
    </HeadlessTab.Group>
  );
}

const Grid = styled.div<{ $tabEnd: number; $contentEnd: number }>((p) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(12, 1fr)',
  '> div:first-child': {
    gridColumnStart: 1,
    gridColumnEnd: p.$tabEnd,
  },
  '> div:last-child': {
    gridColumnStart: p.$tabEnd,
    gridColumnEnd: p.$contentEnd,
  },
  gap: pxToRem(24),
}));

const TabContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',

  '[data-headlessui-focus-visible] &:focus-within': {
    borderRadius: pxToRem(4),
    boxShadow: theme.shadow.focus,
    '--shadow-focus-color': theme.color.elementBlue100,
    '--shadow-focus-gap': 0,
    '--shadow-focus-width': pxToRem(2),
  },
});

const StyledTabItem = styled.button({
  background: 'transparent',
  borderRadius: '0.25rem',
  color: theme.color.textPrimary,
  display: 'flex',
  flexdirection: 'row',
  justifyContent: 'space-between',
  outline: 'none',
  padding: '0.625rem 0.5rem',
  ':hover': {
    cursor: 'pointer',
    backgroundColor: theme.color.uiTrans5,
  },

  '&[data-headlessui-state="selected"]': {
    backgroundColor: theme.color.uiTrans10,
    color: theme.color.elementBlue100,
  },
});
